import React, { useEffect, useRef, useState } from 'react'

import {
  SENTIMENT_POLL_INTERVAL_MS,
  awaitingSentimentKey,
  shouldPollForSentiment,
} from './sentimentBackfill'
import { inboxListView, skeletonRowCount } from './listLoading'
import {
  Tray,
  ChatCircle,
  Envelope,
  PaperPlaneTilt,
  CheckCircle,
  XCircle,
  Brain,
  X,
  CaretLeft,
  CaretRight,
  MagnifyingGlass,
  User,
} from '@phosphor-icons/react'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select, type SelectOption } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'
// §10.1 — the sentiment label/tone/glyph vocabulary lives in one module now.
// The local `sentimentConfig` map that used to sit here duplicated it and drifted
// from it in three ways: `neutral` was muted gray where Analytics had already
// ruled it indigo, `interested` was byte-identical to `positive` where Analytics
// deliberately splits them, and `out_of_office` was relabelled "Unanalyzed" —
// a name Analytics uses for the NULL-sentiment bucket, so the two screens'
// "Unanalyzed" numbers could never be reconciled. Nothing here decides colour.
import { sentimentMeta, sentimentOptions } from '@/lib/sentiment'
import { parseReplyBody } from './replyBody'
import { ReplyBodyView } from './ReplyBodyView'

import { useInboxStore } from '@/stores/inboxStore'
import { usePermission } from '@/hooks/usePermission'
import { usePlanLapse } from '@/hooks/usePlanLapse'
import { inboxApi } from '@/lib/api'
import { formatDistanceToNow } from 'date-fns'
import type { InboxMessage, InboxMailbox, InboxMailboxUser } from '@/types/senderEmail'
import { openEventStream } from '@/lib/sse'
import { coalesce } from '@/lib/coalesce'
import { ALL_MAILBOXES, mailboxIdFor, orderPersonOptions, personFilterForMailbox, personQuery } from './personFilter'
import type { SentimentKey } from '@/lib/sentiment'
import {
  canonicalSentiment,
  intentChipClass,
  intentLabel,
  INTENT_FILTER_OPTIONS,
} from '@/lib/replyVocabulary'

/** Characters of body text a list row previews before it truncates. */
export const PREVIEW_CHAR_LIMIT = 140

export interface MessagePreview {
  /** What the row renders — either the excerpt or the empty-body placeholder. */
  text: string
  /** True when there was no body text at all, so the row can dim the line. */
  isPlaceholder: boolean
}

/**
 * Row preview for a reply body (I1.4).
 *
 * The old inline `msg.body_text?.substring(0, 140)…` appended the ellipsis
 * unconditionally, so a two-word reply read "Sounds good…" (a promise of text
 * that isn't there) and a body-less message rendered as a lone "…". Worse, the
 * slice ran against the raw body: quoted mail is mostly newlines and quote
 * headers, so the first 140 raw characters were often whitespace and the row
 * previewed blank while still showing its ellipsis.
 *
 * So: collapse whitespace runs first, then slice the collapsed string (the 140
 * budget is spent on visible characters, not on `\n`), and only append the
 * ellipsis when something was actually cut. An empty result is reported as a
 * placeholder rather than faked — matching the detail pane's `(No text content)`.
 */
export function messagePreview(
  body?: string | null,
  limit: number = PREVIEW_CHAR_LIMIT,
): MessagePreview {
  // What the person typed, not the quoted thread or its ">" marks (AB#22441).
  const collapsed = parseReplyBody(body).reply.replace(/\s+/g, ' ').trim()
  if (!collapsed) return { text: '(No text content)', isPlaceholder: true }
  return {
    // trimEnd so a cut landing on a space doesn't render "word …".
    text: collapsed.length > limit ? `${collapsed.slice(0, limit).trimEnd()}…` : collapsed,
    isPlaceholder: false,
  }
}

// Sentiment + intent chips both come from the shared vocabulary module, so a
// card's tags always use the same words as the two filters above the list. The
// old local map keyed chips by the RAW stored label, which meant a row returned
// by the "Positive" filter could render a chip reading "Interested".
//
// A stored `out_of_office` sentiment now yields no tone chip at all
// (canonicalSentiment returns null) — it is a delivery class, not a tone. Those
// messages are still identified, by their intent chip reading "Auto-reply".


// The buckets this screen offers, in canonical order. Deliberately NOT the
// stored six: 'interested' / 'not_interested' are intents now, folded into
// Positive / Neutral by the backend. `unanalyzed` IS offerable here because the
// list endpoint maps it to `sentiment IS NULL` (reply_vocabulary).
const INBOX_SENTIMENT_KEYS: SentimentKey[] = [
  'positive', 'neutral', 'negative', 'out_of_office', 'unanalyzed',
]

/**
 * A Reply overview tile while its figures are being refetched.
 *
 * Same box, same padding and the same two rows as the real tile, so the card
 * row keeps its height and the layout does not step when the numbers arrive.
 * Decorative: the figures it stands in for are announced by the tiles
 * themselves once they load, and the row carries aria-busy in the meantime.
 */
function OverviewStatSkeleton() {
  return (
    <div
      className={cn(
        'rounded-xl border bg-white px-4 py-5',
        'border-border/40 shadow-[0_1px_2px_rgba(15,16,17,0.04),0_4px_12px_rgba(15,16,17,0.04)]',
        'dark:bg-[#191a1b]/85 dark:backdrop-blur-sm dark:border-white/[0.08] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_8px_24px_rgba(0,0,0,0.25)]',
      )}
    >
      {/* Stands in for the 28px figure. */}
      <Skeleton variant="text" width="42%" height="28px" />
      <div className="mt-2.5 flex items-center gap-2">
        <Skeleton variant="rectangular" width="24px" height="24px" className="rounded-md flex-shrink-0" />
        <Skeleton variant="text" width="70%" height="12px" />
      </div>
    </div>
  )
}

/**
 * Placeholder for one message row.
 *
 * Same border, padding and four bands as the real card -- sender, subject, a
 * two-line preview and the chip row -- so a column of these keeps the height
 * the list is about to have and nothing steps when the mail arrives.
 */
function MessageRowSkeleton() {
  return (
    <div className="rounded-lg border border-border/60 bg-card dark:bg-white/[0.02] dark:border-white/[0.08]">
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0 space-y-2">
            <Skeleton variant="text" width="38%" height="13px" />
            <Skeleton variant="text" width="62%" height="13px" />
            <div className="space-y-1.5 pt-0.5">
              <Skeleton variant="text" width="100%" height="10px" />
              <Skeleton variant="text" width="78%" height="10px" />
            </div>
            <div className="flex items-center gap-1.5 pt-1">
              <Skeleton variant="rectangular" width="64px" height="16px" className="rounded-full" />
              <Skeleton variant="rectangular" width="52px" height="16px" className="rounded-full" />
            </div>
          </div>
          <Skeleton variant="text" width="52px" height="11px" className="flex-shrink-0" />
        </div>
      </div>
    </div>
  )
}

const InboxPage: React.FC = () => {
  const {
    messages, stats, isLoading, statsLoading, page, pageSize, total,
    fetchMessages, fetchStats,
    // The two selectors live in the store so a route change does not reset
    // them -- see the note on `mailboxValue` there.
    mailboxValue, personValue, setSelection,
    // Pending-approval tab removed (not required) — `pendingApprovals` /
    // `fetchPendingApprovals` are no longer consumed here.
    analyzeMessage, approveReply, rejectReply, sendReply, markAsRead, analyzeUnanalyzed,
  } = useInboxStore()
  const [analyzingAll, setAnalyzingAll] = useState(false)

  const [selectedMessage, setSelectedMessage] = useState<InboxMessage | null>(null)
  // Seeded from the store, which is where the active query already lived, so
  // coming back to the Inbox finds the filters exactly as they were left
  // rather than cleared controls over a filtered list.
  const [sentimentFilter, setSentimentFilter] = useState<string>(
    () => useInboxStore.getState().sentiment || '',
  )
  const [intentFilter, setIntentFilter] = useState<string>(
    () => useInboxStore.getState().intent || '',
  )
  const [emailSearch, setEmailSearch] = useState(
    () => useInboxStore.getState().search || '',
  )
  // Mailbox + per-user filter. The Inbox is always on ONE mailbox -- there is
  // no merged "all mailboxes" view: its people list would be the union of
  // everyone on every shared mailbox and the per-mailbox segregation would be
  // lost. The mailbox selector picks one of the account's mailboxes (from
  // GET /inbox/mailboxes, which every role with inbox.view may call -- the
  // inventory endpoint excludes Analyst, AB#22341); the people selector then
  // narrows to a teammate on that mailbox, the shared Unassigned bucket, or
  // "My replies" (the default). Switching mailbox reloads the sharer list.
  const [mailboxes, setMailboxes] = useState<InboxMailbox[]>([])
  const { allowed: canViewUnassigned } = usePermission('inbox', 'view_unassigned')
  // Everyone except a Representative reads the whole account's replies
  // (RBAC_Policy_Developer_Spec.docx §4-J: Owner/Admin/Manager = All,
  // Analyst = View) so they get an "All members" option that merges every
  // teammate's mail into one list. A Rep is "own"-scoped, so the option would
  // be a no-op for them (identical to "My replies") — hidden rather than
  // shown-but-pointless. Analyst joined the others in AB#22338; their level
  // stays read-only, which is what hides Reply.
  const { scope: inboxViewScope, canWrite: canActOnInbox } = usePermission('inbox', 'view')
  const { lapsed: planLapsed, reason: lapsedTitle } = usePlanLapse()
  const canViewAllMembers = inboxViewScope !== null && inboxViewScope !== 'own'
  const [mailboxUsers, setMailboxUsers] = useState<InboxMailboxUser[]>([])
  const [unassignedCount, setUnassignedCount] = useState(0)
  const [replyContent, setReplyContent] = useState('')
  const [analyzingId, setAnalyzingId] = useState<number | null>(null)
  const [sendingReply, setSendingReply] = useState(false)
  const [activeTab, setActiveTab] = useState<'all' | 'pending'>('all')
  // True while the page is settling on a scope -- boot, or a mailbox switch
  // still fetching the new mailbox's people before it can issue the message
  // request. The store's isLoading covers neither window (no request is in
  // flight yet), and without this the list and the cards sit there showing
  // the mailbox being left as though it were the one being opened.
  // Starts true: the first paint has no mailbox and no request, and the
  // empty state would otherwise greet a full inbox with "No messages yet".
  // Returning to a resolved Inbox has its list and figures already in the
  // store, so it refreshes in the background instead of flashing skeletons
  // over data that is right in front of the reader.
  const [scopeLoading, setScopeLoading] = useState(
    () => !useInboxStore.getState().selectionReady,
  )

  // Load the sharer list for a mailbox and stash its unassigned-bucket size.
  // Returns the roster as well as storing it: a mailbox switch has to know who
  // is on the new mailbox before it can decide whether the current person
  // filter still means anything (personFilterForMailbox).
  // `undefined` asks for the union across every mailbox the viewer shares,
  // which is what the merged view needs: the same roster the merged list is
  // attributed to, deduped per member with their counts summed.
  const loadMailboxUsers = async (senderEmailId?: number): Promise<InboxMailboxUser[]> => {
    try {
      const res = await inboxApi.getMailboxUsers({ sender_email_id: senderEmailId })
      setMailboxUsers(res.data.items)
      setUnassignedCount(res.data.unassigned_count)
      return res.data.items
    } catch {
      setMailboxUsers([])
      setUnassignedCount(0)
      return []
    }
  }

  // Boot: open on the account's default mailbox (falling back to the first),
  // then load that mailbox's people and messages. With no mailbox at all the
  // list is simply empty -- there is nothing to read replies on. Runs exactly
  // once, guarded by a ref rather than an empty dependency array — it has to
  // WAIT for the permission map, and the mount pass can happen before
  // authStore has it.
  //
  // Why waiting matters: the default person bucket below reads the map, and a
  // missing map reads as "no access" — firing early would open every role on
  // the merged reply view instead of only the read-only one. Every role holds
  // an `inbox.view` cell,
  // so a null scope means "not loaded yet", never "denied" (AB#22338).
  const bootedRef = useRef(false)
  useEffect(() => {
    if (bootedRef.current || inboxViewScope === null) return
    bootedRef.current = true
    // A read-only role is attributed no mail of its own — replies belong to
    // whoever sent them — so "My replies" would open on an empty list. Start
    // them on the merged view instead.
    const fallbackPerson = canActOnInbox ? 'me' : 'all-members'
    // Resuming: the store still holds the mailbox, the person and the filters
    // from the last visit. Keep them -- the whole point of holding them -- and
    // only re-check that they still mean something.
    const resuming = useInboxStore.getState().selectionReady
    const person = resuming ? personValue : fallbackPerson
    if (!resuming) setSelection({ personValue: person })
    const allMembers = person === 'all-members'
    ;(async () => {
      let boxes: InboxMailbox[] = []
      try {
        boxes = (await inboxApi.getMailboxes()).data
      } catch {
        boxes = []
      }
      setMailboxes(boxes)
      // A kept mailbox has to still exist: one can be deleted, or the account
      // switched, while the reader was on another page. Gone means fall
      // through to a fresh open rather than querying an id nobody can read.
      const keptIsReal =
        resuming &&
        (mailboxValue === ALL_MAILBOXES || boxes.some((b) => String(b.id) === mailboxValue))
      if (keptIsReal) {
        const roster = await loadMailboxUsers(mailboxIdFor(mailboxValue))
        // The roster can have changed too (a teammate with no mail here any
        // more), so the kept person goes through the same check a mailbox
        // switch uses.
        const kept = personFilterForMailbox(person, roster, fallbackPerson)
        if (kept === person) {
          // Nothing moved: refresh behind the list already on screen.
          fetchMessages(undefined, { background: true })
        } else {
          setSelection({ personValue: kept })
          fetchMessages(personQuery(kept))
        }
        fetchStats()
        setScopeLoading(false)
        return
      }
      const def = boxes.find((b) => b.is_default) || boxes[0]
      if (def) {
        setSelection({ mailboxValue: String(def.id), personValue: person })
        await loadMailboxUsers(def.id)
        fetchMessages({ sender_email_id: def.id, contact_user_id: undefined, unassigned: false, all_members: allMembers })
      } else {
        // No mailbox to open: an empty list, not a merged view.
        setMailboxUsers([])
        fetchMessages({ sender_email_id: undefined, contact_user_id: undefined, unassigned: false, all_members: allMembers })
      }
      fetchStats()
      // Hand the loading state over. Both calls above raise their own flags
      // synchronously, so there is no frame where nothing is loading -- and
      // a mailbox request that failed still reaches here, so the skeletons
      // can never be stranded on screen.
      setScopeLoading(false)
      setSelection({})  // resolved: a later visit resumes instead of re-opening
      // Pending-approval tab removed — no longer prefetch approvals.
      // fetchPendingApprovals()
    })()
  }, [inboxViewScope, canActOnInbox])

  // Live updates. The backend pushes a bare "changed" signal (GET
  // /inbox/stream) whenever an InboxMessage in this account is stored or
  // updated -- the poller filing mail, sentiment landing, a teammate's
  // reply or mark-as-read. The page answers by re-running its own list,
  // stats and people requests with whatever filters are active, in the
  // background (no spinner, selection kept), so nothing shown here comes
  // from the stream itself. Bursts are coalesced on both sides.
  //
  // reconnectOnClose: the server never ends this stream on purpose, so a
  // clean close is a deploy or a proxy cutoff -- reconnect, don't freeze.
  // If the stream cannot be held at all (Redis down, a proxy that blocks
  // streaming), fall back to a 30s background poll.
  //
  // The mailbox id is read through a ref so a mailbox switch does not tear
  // down and re-open the stream.
  const mailboxValueRef = useRef(mailboxValue)
  mailboxValueRef.current = mailboxValue
  useEffect(() => {
    if (inboxViewScope === null) return
    const refresh = coalesce(() => {
      fetchMessages(undefined, { background: true })
      fetchStats()
      const id = Number(mailboxValueRef.current)
      if (id) loadMailboxUsers(id)
    }, 750)
    let pollId: number | undefined
    const closeStream = openEventStream('/inbox/stream', {
      reconnectOnClose: true,
      onMessage: (data) => {
        if (data?.changed) refresh.trigger()
      },
      onError: () => {
        if (pollId === undefined) pollId = window.setInterval(() => refresh.trigger(), 30000)
      },
    })
    return () => {
      closeStream()
      refresh.cancel()
      if (pollId !== undefined) window.clearInterval(pollId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxViewScope])

  // Fill in sentiment/intent badges that were not ready when the list loaded.
  //
  // The mailbox poll commits new messages and THEN classifies them one LLM
  // call at a time, so mail is visible for several seconds before its
  // sentiment exists. Nothing told the open inbox to look again, so a reply
  // sat there unclassified until the user refreshed the page (AB#22328).
  // Committing first is correct — mail must not wait on a slow LLM — so the
  // page quietly refetches while anything on screen is still pending.
  //
  // Bounded by shouldPollForSentiment: some messages never get a sentiment,
  // and a list that will never settle must not be polled forever.
  const sentimentAttemptsRef = useRef(0)
  const pendingKey = awaitingSentimentKey(messages)
  useEffect(() => {
    // A different set of unclassified messages gets a fresh budget rather
    // than inheriting an exhausted one from the previous list.
    sentimentAttemptsRef.current = 0
  }, [pendingKey])
  useEffect(() => {
    const hidden = typeof document !== 'undefined' && document.hidden
    if (!shouldPollForSentiment(messages, sentimentAttemptsRef.current, hidden)) return
    const timer = setTimeout(() => {
      sentimentAttemptsRef.current += 1
      // Background: keep the current list on screen and just let the badges
      // appear, rather than flashing a spinner every few seconds.
      fetchMessages(undefined, { background: true })
    }, SENTIMENT_POLL_INTERVAL_MS)
    return () => clearTimeout(timer)
  }, [messages, pendingKey, fetchMessages])

  // Debounced sender search (name or email) — refetch (resetting to page 0) once the
  // user pauses typing. Skips the initial render so it doesn't duplicate the
  // mount fetch above.
  const emailSearchMounted = useRef(false)
  useEffect(() => {
    if (!emailSearchMounted.current) {
      emailSearchMounted.current = true
      return
    }
    const timer = setTimeout(() => {
      fetchMessages({ search: emailSearch.trim() || undefined })
    }, 350)
    return () => clearTimeout(timer)
  }, [emailSearch])

  const handleAnalyze = async (id: number) => {
    setAnalyzingId(id)
    try {
      await analyzeMessage(id)
      // Pending-approval tab removed — no approvals list to refresh.
      // await fetchPendingApprovals()
    } finally {
      setAnalyzingId(null)
    }
  }

  const handleSendReply = async (id: number) => {
    if (!replyContent.trim()) return
    setSendingReply(true)
    try {
      await sendReply(id, replyContent)
      setReplyContent('')
      setSelectedMessage(null)
    } finally {
      setSendingReply(false)
    }
  }

  const handleFilter = (sentiment: string) => {
    setSentimentFilter(sentiment)
    // Always send the key: the store only overrides its persisted filter when
    // 'sentiment' is present in params, so omitting it on "All sentiments"
    // would leave the previous filter stuck.
    fetchMessages({ sentiment: sentiment || undefined })
  }

  // Intent is a second, independent axis — the two filters AND together
  // server-side, so "Positive + Objection" is a valid (and useful) narrowing.
  const handleIntentFilter = (intent: string) => {
    setIntentFilter(intent)
    fetchMessages({ intent: intent || undefined })
  }

  // Switch mailbox: keep the person filter if it still means something on the
  // new mailbox, reload the sharer list, and refetch.
  //
  // It used to snap back to "My replies" every time. On a shared mailbox that
  // threw away a selection the reader had just made, so changing which mailbox
  // they were reading silently changed whose replies they were reading too.
  // The roster is loaded FIRST and the decision made against it, so a teammate
  // with no mail on the new mailbox still falls back rather than asking the API
  // for a combination that does not exist (personFilterForMailbox).
  const onMailboxChange = async (v: string) => {
    // Re-picking the open mailbox changes nothing: no re-render, no refetch,
    // and the open message stays open.
    if (v === mailboxValue) return
    setSelection({ mailboxValue: v })
    // The roster has to land before the message request can be issued, and
    // until it does nothing is in flight for isLoading to report. Cover that
    // window explicitly so the switch shows placeholders from the click
    // rather than from whenever the second request starts.
    setScopeLoading(true)
    // Whoever is open in the detail pane belongs to the mailbox being left.
    setSelectedMessage(null)
    const id = mailboxIdFor(v)
    // Never throws: it swallows its own failure and answers with an empty
    // roster, which personFilterForMailbox reads as "fall back".
    const roster = await loadMailboxUsers(id)
    // This role's default bucket -- "My replies", or the merged view for a
    // read-only role that is attributed no mail of its own (AB#22338).
    const fallback = canActOnInbox ? 'me' : 'all-members'
    const person = personFilterForMailbox(personValue, roster, fallback)
    setSelection({ personValue: person })
    // Same mapping the person selector uses, so switching mailbox while holding
    // a person cannot fetch something subtly different from selecting that
    // person directly.
    // `undefined` for the merged view: the API reads an absent mailbox as
    // "every mailbox this viewer shares", which is the same set the roster
    // above was built from.
    fetchMessages({ sender_email_id: id, ...personQuery(person) })
    // Recompute the overview cards for the new scope (fetchMessages has
    // already committed the new filters to the store synchronously).
    fetchStats()
    // Released only once both requests are in flight, so the placeholders
    // hand over to isLoading/statsLoading instead of blinking off between
    // the two.
    setScopeLoading(false)
  }

  // Switch the per-user filter within the current mailbox. 'me' = my replies,
  // 'unassigned' = shared no-owner bucket, 'all-members' = everyone this role
  // may read merged into one list, any other value = that teammate's id.
  const onPersonChange = (v: string) => {
    if (v === personValue) return
    setSelection({ personValue: v })
    setSelectedMessage(null)
    fetchMessages(personQuery(v))
    // Recompute the overview cards for the selected person.
    fetchStats()
  }

  // Mailbox selector options: a merged view first, then the account's
  // mailboxes one by one.
  //
  // The merged option was deliberately absent at first, on the grounds that its
  // people list would be the union of everyone on every shared mailbox and the
  // per-mailbox segregation would be lost. It is not lost: the roster endpoint
  // answers for the union when no mailbox is named, bounded by exactly the same
  // visibility rules as the single-mailbox case, so the merged view can only
  // ever show what the individual views already would.
  //
  // Offered only when there is more than one mailbox to merge -- on a single
  // mailbox it would be a second name for the same list.
  const mailboxOptions = [
    ...(mailboxes.length > 1 ? [{ value: ALL_MAILBOXES, label: 'All mailboxes' }] : []),
    ...mailboxes.map((b) => ({ value: String(b.id), label: b.email_address })),
  ]

  // One person-dropdown entry for a mailbox-roster row (self or a teammate).
  // Name on the top line; the current user is flagged with a small accent dot
  // after the name (never an unprofessional "Me"). The email drops to the
  // muted sub-line via `description`.
  const personOptionFor = (u: InboxMailboxUser) => {
    const name = u.display_name || u.email || `User ${u.user_id}`
    return {
      value: u.is_self ? 'me' : String(u.user_id),
      label: (
        // A former member is greyed, not disabled: their replies are still
        // the team's history and must stay openable. The chip says why the
        // name looks different, so it reads as "was here" rather than broken.
        <span className={`inline-flex items-center gap-1.5 min-w-0${u.is_former ? ' text-muted-foreground dark:text-[#8a8f98]' : ''}`}>
          <span className="truncate">{name}</span>
          {u.is_former && (
            <span
              className="flex-shrink-0 rounded-full border border-border/60 dark:border-white/[0.10] px-1.5 py-px text-[10px] uppercase tracking-wide text-muted-foreground/80 dark:text-[#8a8f98]"
              style={{ fontWeight: 560, letterSpacing: '0.06em' }}
            >
              Former member
            </span>
          )}
          {u.is_self && (
            <span
              className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#5e6ad2] dark:bg-[#7170ff]"
              aria-label="You"
            />
          )}
          {u.message_count > 0 && (
            <span className="flex-shrink-0 text-muted-foreground dark:text-[#8a8f98] tabular-nums">
              ({u.message_count})
            </span>
          )}
        </span>
      ),
      description: u.email && u.email !== name ? u.email : undefined,
    }
  }

  // People selector options: everyone with mail on the selected mailbox (self
  // first) + Unassigned + All members.
  const unassignedLabel = unassignedCount ? `Unassigned (${unassignedCount})` : 'Unassigned'
  // Triaging the shared no-owner bucket is Owner/Admin only
  // (inbox.view_unassigned); the API rejects the flag for everyone else, so
  // the option must not be offered to them either.
  const unassignedOption = canViewUnassigned
    ? [{ value: 'unassigned', label: unassignedLabel }]
    : []
  // First in the list — the broadest scope, read before narrowing to one
  // person, which is also where "All mailboxes" sits in the selector beside
  // it. Hidden entirely for a Rep: their inbox.view scope is "own", so the
  // option would just duplicate "My replies".
  const allMembersOption = canViewAllMembers
    ? [{ value: 'all-members', label: 'All members' }]
    : []
  const selfOption = mailboxUsers.filter((u) => u.is_self).map(personOptionFor)
  const teammateOptions = mailboxUsers.filter((u) => !u.is_self).map(personOptionFor)
  // Explicit type argument: the groups are a mix of plain-label options and
  // rich ones (a teammate's name carries a chip and an email sub-line), and
  // inference would otherwise pin T to whichever group comes first.
  const personOptions = orderPersonOptions<SelectOption>({
    allMembers: allMembersOption,
    self: selfOption.length ? selfOption : [{ value: 'me', label: 'My replies' }],
    teammates: teammateOptions,
    unassigned: unassignedOption,
  })

  // The overview cards, lifted out of the JSX so the loading placeholders
  // can be generated from the same list the cards are. Hard-coding a count
  // would let the two drift the moment a sentiment is added.
  const overviewStats = [
            {
              label: 'Total replies',
              value: stats.total,
              icon: ChatCircle,
              tint: { bg: 'bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12]', fg: 'text-[#5e6ad2] dark:text-[#a5b4fc]' },
            },
            {
              label: 'Unread',
              value: stats.unread,
              icon: Envelope,
              tint: { bg: 'bg-amber-500/10 dark:bg-amber-500/[0.10]', fg: 'text-amber-600 dark:text-amber-400' },
            },
            // Reads sentiment_summary — the canonical rollup — NOT the raw
            // sentiment_breakdown. The stored labels split one tone across two
            // tiles ('positive' excluded 'interested', 'negative' excluded
            // 'not_interested') and omitted out_of_office entirely, so
            // Positive + Neutral + Negative could never reach Total replies.
            // These five buckets partition every reply, so they sum to Total
            // exactly and each tile matches the filter option of the same name.
            // Label, glyph and tint still come from the §10.1 vocabulary.
            ...INBOX_SENTIMENT_KEYS.map((key) => {
              const meta = sentimentMeta(key)
              return {
                label: meta.label,
                value: stats.sentiment_summary?.[key] ?? 0,
                icon: meta.Icon,
                tint: meta.iconTint,
              }
            }),
  ]

  // Pending-approval tab removed — the list always shows the "All messages" set.
  const displayMessages = messages

  // Total matching the current "All messages" query — from the X-Total-Count
  // header on the list response, so it reflects every active filter (sentiment
  // + intent + email search). Falls back to stats-derived numbers until the
  // first list fetch resolves.
  //
  // The fallback reads sentiment_summary, NOT sentiment_breakdown: the dropdown
  // now sends canonical values, and breakdown is keyed by the raw stored labels
  // — so 'positive' there would under-count by every 'interested' row. With
  // both filters active there is no single stats key to read, so fall back to 0
  // (the header lands a moment later and corrects it).
  const filterCount = intentFilter
    ? (sentimentFilter ? 0 : stats.intent_breakdown?.[intentFilter] ?? 0)
    : stats.sentiment_summary?.[sentimentFilter] ?? 0
  const totalCount = total ?? ((sentimentFilter || intentFilter) ? filterCount : stats.total)
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  // One answer to "is the message column still resolving", shared by the
  // list, its pagination and the screen-reader summary so the three cannot
  // disagree. Background refetches (SSE, sentiment backfill) never raise
  // isLoading, so live updates keep landing without the rows flashing.
  const listView = inboxListView({
    foregroundFetch: isLoading,
    switchingScope: scopeLoading,
    count: displayMessages.length,
  })
  const statsPending = statsLoading || scopeLoading

  const goToPage = (p: number) => {
    fetchMessages({ page: Math.max(0, Math.min(p, totalPages - 1)) })
  }

  // If the current page falls past the end after the total shrinks (e.g. the
  // last item on the last page was replied to and dropped out of the filter),
  // step back so the user never lands on a blank page.
  useEffect(() => {
    if (activeTab === 'all' && !isLoading && page > 0 && page > totalPages - 1) {
      goToPage(totalPages - 1)
    }
  }, [activeTab, isLoading, page, totalPages])

  // Close the detail pane when the selected message is no longer in the visible
  // list — sentiment filter changed, tab switched, or it dropped out after a reply.
  // Without this the right pane keeps showing a message the filter excludes.
  useEffect(() => {
    if (selectedMessage && !displayMessages.some((m) => m.id === selectedMessage.id)) {
      setSelectedMessage(null)
    }
  }, [displayMessages, selectedMessage])

  // Detail-pane sentiment chip, resolved off the same §10.1 map as the row chip
  // so the two can never spell or tone the same value differently. The old code
  // fell through to the raw backend string (a bare "not_interested") with no tone
  // at all whenever the local map had no entry; `sentimentMeta` always resolves —
  // an unrecognised value degrades to a muted chip with a humanized label rather
  // than borrowing another sentiment's colour.
  const selectedSentiment = selectedMessage?.sentiment
    ? sentimentMeta(selectedMessage.sentiment)
    : null

  // Live-region text for the list. Three things silently rewrite this list — the
  // first load, "Analyze all", and pagination — and until now a screen-reader
  // user got no signal from any of them: rows just swapped underneath them.
  //
  // Deliberately blank while the *first* page is in flight, because the spinner
  // that replaces the list in that state is itself a `role="status"` with a
  // label; two regions both saying "Loading" would announce it twice. A refetch
  // that keeps the old rows on screen (pagination, filter change) shows no
  // spinner, so nothing is announced mid-flight and the settled text carries the
  // new page number — which is the announcement that actually matters.
  const listStatus = analyzingAll
    ? 'Analyzing unanalyzed replies'
    : listView === 'loading'
      ? ''
      : displayMessages.length === 0
        ? emailSearch.trim() || sentimentFilter
          ? 'No messages match the current filters'
          : 'No messages yet'
        : `${displayMessages.length} of ${totalCount} messages, page ${page + 1} of ${totalPages}`

  return (
    <div className="space-y-5">
      {/* Header — §23 */}
      <div>
        <h1
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
        >
          Inbox
        </h1>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] mt-1"
          style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
        >
          Monitor replies and manage auto-responses.
        </p>
      </div>

      {/* Hero stats block — title + decorative reply-convergence motif + floating stat pills (§21 / §51) */}
      <section
        className={cn(
          'relative overflow-hidden rounded-2xl border border-border/50 p-5 sm:p-6',
          'bg-gradient-to-br from-white via-[#f6f7ff] to-[#eef0ff]',
          'dark:border-white/[0.08] dark:from-[#0f1011] dark:via-[#131421] dark:to-[#1a1b2e]',
        )}
      >
        {/* Decoration — "converging replies": a fan of quadratic curves that all
            terminate at one focal point just off the upper-right corner, with a
            single radial glow at that same origin. Reads as inbound traffic
            arriving at one place, which is what this route is. Motif is
            unclaimed — Campaigns owns concentric rings, Contacts the
            constellation network, Workflows the gradient mesh.

            Two constraints shape the geometry, both learned the hard way:

            1. The wrapper is right-anchored and width-capped (`right-0 w-2/3
               max-w-[560px]`), not `inset-0`. With a full-bleed wrapper this
               panel is so wide and short that `slice` crops the viewBox to a
               thin horizontal band and stretches it across the whole panel — the
               mid-fan curves then ran straight through the h2 and its
               description, which is exactly what a decoration must never do.
            2. Inside that box, `#ix-fan-fade` masks the strokes to nothing left
               of x≈210, so on a narrow viewport — where the box overlaps the
               title's own column — the curves have already faded out before they
               reach any glyph. It also means the curves can start inside the
               canvas without showing a cut terminus.

            One glow, no halftone: a second texture would be the layer-stacking
            §51 warns about. `currentColor` throughout so one SVG serves both
            modes off the parent text class. */}
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-2/3 max-w-[560px] overflow-hidden text-[#7170ff] dark:text-[#7170ff]"
          aria-hidden="true"
        >
          <svg
            viewBox="0 0 560 240"
            preserveAspectRatio="xMaxYMid slice"
            className="absolute inset-0 h-full w-full opacity-90 dark:opacity-70"
          >
            <defs>
              {/* Halo at the convergence point — the only fill in the motif. */}
              <radialGradient id="ix-focal-glow" cx="88%" cy="2%" r="58%">
                <stop offset="0%"   stopColor="currentColor" stopOpacity="0.18" />
                <stop offset="55%"  stopColor="currentColor" stopOpacity="0.05" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
              </radialGradient>

              {/* Horizontal falloff in user space: fully transparent at x=210,
                  fully opaque by x=520. Every stroke below inherits it. */}
              <linearGradient id="ix-fan-fade" gradientUnits="userSpaceOnUse" x1="210" y1="0" x2="520" y2="0">
                <stop offset="0%"   stopColor="white" stopOpacity="0" />
                <stop offset="45%"  stopColor="white" stopOpacity="0.45" />
                <stop offset="100%" stopColor="white" stopOpacity="1" />
              </linearGradient>
              <mask id="ix-fan-mask" maskUnits="userSpaceOnUse" x="0" y="0" width="560" height="240">
                <rect width="560" height="240" fill="url(#ix-fan-fade)" />
              </mask>
            </defs>

            <rect width="100%" height="100%" fill="url(#ix-focal-glow)" />

            {/* The fan. Every curve resolves to the same off-canvas focal point
                (566,4); the control x steps left as the entry y drops, which
                opens the fan instead of drawing five parallels. Stroke opacity
                steps 0.34 → 0.06 outward from the focal axis, so the lower
                curves — the ones that pass behind the stat pills — are already
                the faintest lines on the panel. */}
            <g fill="none" stroke="currentColor" strokeLinecap="round" mask="url(#ix-fan-mask)">
              <path d="M 150 40  Q 452 40 566 4"  strokeOpacity="0.34" strokeWidth="1.1" />
              <path d="M 150 76  Q 442 76 566 4"  strokeOpacity="0.26" strokeWidth="1.1" />
              <path d="M 150 116 Q 430 116 566 4" strokeOpacity="0.18" strokeWidth="1" />
              <path d="M 150 162 Q 418 162 566 4" strokeOpacity="0.11" strokeWidth="1" />
              <path d="M 150 212 Q 404 212 566 4" strokeOpacity="0.06" strokeWidth="1" />
            </g>
          </svg>
        </div>

        {/* Title block */}
        <div className="relative z-10 max-w-md">
          <h2
            className="text-foreground dark:text-[#f7f8f8]"
            style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.3 }}
          >
            Reply overview
          </h2>
          <p
            className="text-muted-foreground dark:text-[#8a8f98] mt-1"
            style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
          >
            Snapshot of inbound replies, sentiment, and pending auto-responses.
          </p>
        </div>

        {/* Stat pills — float over the decoration. Every sentiment pill takes its
            glyph and tint straight from the §10.1 map, so a pill can no longer
            disagree with the row chip for the same sentiment 200 lines below.
            Before this, the pills invented three chromatics nothing else in the
            product uses — teal for positive, rose for negative, slate for neutral
            — while the chips rendered the same three as emerald, red and muted.
            The list is derived from SENTIMENT_ORDER rather than hand-written so a
            seventh sentiment can never be added to the vocabulary and forgotten
            here. 'Total replies' is an aggregate rather than a sentiment, so it
            stays brand indigo, and 'Unread' keeps amber — a held/needs-attention
            state, which is the sanctioned `warning` tone. */}
        <div
          className="relative z-10 mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3"
          aria-busy={statsPending}
        >
          {(statsPending
            // One placeholder per real card, mapped from the same list, so
            // the row cannot load as five tiles and settle as seven.
            // Changing the mailbox or the person refetches these, and
            // leaving the previous scope's figures up while that happens
            // reads as the numbers being wrong rather than stale.
            ? overviewStats.map((stat) => <OverviewStatSkeleton key={stat.label} />)
            : overviewStats.map((stat) => (
            <div
              key={stat.label}
              className={cn(
                'rounded-xl border bg-white px-4 py-5 transition-colors duration-150',
                'border-border/40 shadow-[0_1px_2px_rgba(15,16,17,0.04),0_4px_12px_rgba(15,16,17,0.04)]',
                'dark:bg-[#191a1b]/85 dark:backdrop-blur-sm dark:border-white/[0.08] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_8px_24px_rgba(0,0,0,0.25)]',
              )}
            >
              <p
                className="text-foreground dark:text-[#f7f8f8] tabular-nums"
                style={{ fontSize: '28px', fontWeight: 590, letterSpacing: '-0.5px', lineHeight: 1.05 }}
              >
                {stat.value}
              </p>
              <div className="mt-2.5 flex items-center gap-2">
                <div
                  className={cn('h-6 w-6 rounded-md flex items-center justify-center flex-shrink-0', stat.tint.bg)}
                  aria-hidden="true"
                >
                  <stat.icon className={cn('h-3.5 w-3.5', stat.tint.fg)} weight="regular" />
                </div>
                <p
                  className="text-muted-foreground dark:text-[#8a8f98] min-w-0 truncate"
                  style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                  title={stat.label}
                >
                  {stat.label}
                </p>
              </div>
            </div>
          )))}
        </div>
      </section>

      {/* Tabs + Filters */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <TabPill
            active={activeTab === 'all'}
            onClick={() => setActiveTab('all')}
            label="All messages"
          />
          {/* Pending-approval tab removed (not required).
          <TabPill
            active={activeTab === 'pending'}
            onClick={() => { setActiveTab('pending'); fetchPendingApprovals() }}
            label="Pending approval"
            count={stats.pending_approval}
          />
          */}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-64">
            <Input
              value={emailSearch}
              onChange={(e) => setEmailSearch(e.target.value)}
              placeholder="Search by name or email…"
              icon={<MagnifyingGlass className="h-4 w-4" weight="regular" />}
              iconPosition="left"
              aria-label="Search by sender name or email address"
              className="h-9"
            />
          </div>
          {/* Mailbox → person filter, joined into one segmented control so the
              "who" reads as scoped by the "which mailbox". */}
          <div
            className={cn(
              'inline-flex items-center h-9 rounded-lg border overflow-hidden',
              'border-border/60 bg-background',
              'dark:border-white/[0.08] dark:bg-white/[0.02]',
              'focus-within:border-[#7170ff]/55 dark:focus-within:border-[#7170ff]/75 transition-colors',
            )}
          >
            <Select
              value={mailboxValue}
              onChange={onMailboxChange}
              ariaLabel="Mailbox"
              placeholder="No mailbox"
              icon={<Envelope className="h-4 w-4" weight="regular" />}
              options={mailboxOptions}
              minWidth={188}
              buttonClassName="border-transparent bg-transparent rounded-none hover:bg-muted/40 dark:bg-transparent dark:hover:bg-white/[0.04]"
            />
            <div className="h-5 w-px flex-shrink-0 bg-border/60 dark:bg-white/[0.08]" aria-hidden="true" />
            <Select
              value={personValue}
              onChange={onPersonChange}
              ariaLabel="Filter by user"
              icon={<User className="h-4 w-4" weight="regular" />}
              options={personOptions}
              minWidth={164}
              buttonClassName="border-transparent bg-transparent rounded-none hover:bg-muted/40 dark:bg-transparent dark:hover:bg-white/[0.04]"
            />
          </div>
          {/* Classification is an LLM call the account pays for, so it needs a
              write-capable level on inbox.view -- an Analyst "spends no
              credits" (spec §2) and the route refuses them (AB#22338). */}
          {canActOnInbox && (
            <Button
              variant="outline"
              onClick={async () => {
                setAnalyzingAll(true)
                try { await analyzeUnanalyzed() } finally { setAnalyzingAll(false) }
              }}
              disabled={analyzingAll}
              loading={analyzingAll}
              icon={<Brain className="h-4 w-4" weight="regular" />}
              title="Classify all unanalyzed campaign-matched replies"
            >
              Analyze all
            </Button>
          )}
          {/* Two independent filters (Reply Module spec §3). Sentiment offers
              the three canonical tones plus the two non-tone buckets; the
              backend fans each tone out to the stored labels it covers, so
              "Positive" still reaches rows stored as 'interested'. Engagement
              moved to the Intent filter, where "Interested" and "Not
              interested" now live. Labels come from the §10.1 vocabulary, so
              the dropdown, the hero pills and the row chips cannot spell a
              sentiment two ways. */}
          <Select
            value={sentimentFilter}
            onChange={(v) => handleFilter(v)}
            ariaLabel="Sentiment filter"
            // Canonical keys only — the raw 'interested'/'not_interested' are
            // intents now, and `unanalyzed` IS offerable because the backend
            // maps it to `sentiment IS NULL` (reply_vocabulary).
            options={[{ value: '', label: 'All sentiments' }, ...sentimentOptions(INBOX_SENTIMENT_KEYS)]}
            minWidth={180}
          />
          <Select
            value={intentFilter}
            onChange={(v) => handleIntentFilter(v)}
            ariaLabel="Intent filter"
            // Lives in replyVocabulary alongside the labels and chip classes so
            // the filter's vocabulary can't drift from what the cards render.
            options={INTENT_FILTER_OPTIONS}
            minWidth={190}
          />
        </div>
      </div>

      {/* Messages grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* List column */}
        <div className="space-y-2.5 p-1 -m-1">
          {/* A text-only summary, deliberately NOT a wrapper around the list —
              making the rows themselves the live region would re-announce every
              row on every refetch. */}
          <span className="sr-only" role="status" aria-live="polite">
            {listStatus}
          </span>

          {listView === 'loading' ? (
            // Placeholder rows, not a centred spinner: the spinner only
            // appeared on an empty column, so every mailbox and person switch
            // kept the previous scope's mail up through the refetch and it
            // read as settled. The count matches what is already on screen so
            // the column holds its height across the swap.
            <div className="space-y-2.5" role="status" aria-label="Loading messages">
              {Array.from({ length: skeletonRowCount(displayMessages.length, pageSize) }).map((_, i) => (
                <MessageRowSkeleton key={i} />
              ))}
            </div>
          ) : displayMessages.length === 0 ? (
            <EmptyCard
              icon={<Tray className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />}
              headline={
                activeTab === 'pending'
                  ? 'No pending approvals'
                  : emailSearch.trim() || sentimentFilter || intentFilter
                    ? 'No matching messages'
                    : 'No messages yet'
              }
              body={
                activeTab === 'pending'
                  ? 'All auto-replies have been handled.'
                  : emailSearch.trim() || sentimentFilter || intentFilter
                    ? 'Try a different name or email address, or clear the filters.'
                    : 'Replies to your campaigns will appear here.'
              }
            />
          ) : (
            displayMessages.map((msg) => {
              // Chip the CANONICAL tone, not the stored label: a row returned
              // by the "Positive" filter must not render "Interested".
              // out_of_office has no tone but is still worth naming, so it maps
              // to itself. Unclassified rows get no chip — the detail pane's
              // "Analyze sentiment" button already signals that.
              const toneKey = canonicalSentiment(msg.sentiment)
                ?? (msg.sentiment === 'out_of_office' ? 'out_of_office' : null)
              const sent = toneKey ? sentimentMeta(toneKey) : null
              const intentText = intentLabel(msg.intent)
              const preview = messagePreview(msg.body_text)
              const active = selectedMessage?.id === msg.id
              return (
                <button
                  key={msg.id}
                  type="button"
                  onClick={() => {
                    // Clicking the open message again is not a change.
                    if (selectedMessage?.id !== msg.id) setSelectedMessage(msg)
                    if (!msg.is_read && canActOnInbox) markAsRead(msg.id)
                  }}
                  aria-pressed={active}
                  className={cn(
                    'w-full text-left rounded-lg border transition-colors duration-150',
                    active
                      ? 'border-[#5e6ad2] bg-[#5e6ad2]/[0.06] dark:bg-[#7170ff]/[0.08] dark:border-[#7170ff]/70'
                      : 'border-border/60 bg-card hover:bg-muted/30 hover:border-border/80 dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.14]',
                  )}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span
                            className="text-foreground dark:text-[#f7f8f8] truncate"
                            style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
                          >
                            {msg.from_name || msg.from_address}
                          </span>
                          {!msg.is_read && (
                            <span className="h-1.5 w-1.5 rounded-full bg-[#5e6ad2] dark:bg-[#7170ff] flex-shrink-0" />
                          )}
                        </div>
                        <p
                          className="text-foreground dark:text-[#f7f8f8] truncate"
                          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                        >
                          {msg.subject || '(No subject)'}
                        </p>
                        <p
                          className={cn(
                            'mt-1 line-clamp-2',
                            preview.isPlaceholder
                              ? 'text-muted-foreground/70 dark:text-[#62666d]'
                              : 'text-muted-foreground dark:text-[#8a8f98]',
                          )}
                          style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
                        >
                          {preview.text}
                        </p>
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                          {sent && (
                            <span
                              className={cn(
                                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border',
                                sent.borderChipClass,
                              )}
                              style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.04em' }}
                            >
                              <sent.Icon className="h-3 w-3 flex-shrink-0" weight="regular" aria-hidden="true" />
                              {sent.label}
                            </span>
                          )}
                          {intentText && (
                            <span
                              className={cn(
                                'inline-flex items-center px-2 py-0.5 rounded-full border',
                                intentChipClass(msg.intent),
                              )}
                              style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '0.04em' }}
                            >
                              {intentText}
                            </span>
                          )}
                          {msg.auto_reply_pending && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full border bg-amber-500/10 text-amber-700 border-amber-500/25 dark:bg-amber-500/[0.12] dark:text-amber-400 dark:border-amber-500/25"
                              style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '0.04em' }}
                            >
                              Pending approval
                            </span>
                          )}
                          {msg.auto_reply_sent && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full border bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:bg-emerald-500/[0.12] dark:text-emerald-400 dark:border-emerald-500/25"
                              style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '0.04em' }}
                            >
                              Replied
                            </span>
                          )}
                        </div>
                      </div>
                      <span
                        className="text-muted-foreground dark:text-[#8a8f98] flex-shrink-0 tabular-nums whitespace-nowrap"
                        style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                      >
                        {msg.received_at ? formatDistanceToNow(new Date(msg.received_at), { addSuffix: true }) : ''}
                      </span>
                    </div>

                    {/* Campaign name — bottom of the card, flush right. Kept out
                        of the tag-chip row above so its position never drifts
                        with however many tags a message happens to carry; a
                        column of cards lines these up in one place. max-w +
                        truncate stop a long campaign name from wrapping, with
                        the full value on hover. */}
                    {msg.campaign_name && (
                      <div className="flex justify-end mt-2">
                        <span
                          className="max-w-[70%] truncate px-2 py-0.5 rounded-full border bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30"
                          style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '0.04em' }}
                          title={msg.campaign_name}
                        >
                          {msg.campaign_name}
                        </span>
                      </div>
                    )}
                  </div>
                </button>
              )
            })
          )}

          {/* Pagination — only on the "All messages" tab; the pending-approvals
              endpoint returns its full (small) set unpaginated. */}
          {activeTab === 'all' && listView !== 'loading' && totalCount > pageSize && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={totalCount}
              onPageChange={goToPage}
            />
          )}
        </div>

        {/* Detail column */}
        <div>
          {selectedMessage ? (
            <Card
              variant="default"
              className="sticky top-4 rounded-lg border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08]"
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <h3
                    className="text-foreground dark:text-[#f7f8f8] flex-1 min-w-0"
                    style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px', lineHeight: 1.3 }}
                  >
                    {selectedMessage.subject || '(No subject)'}
                  </h3>
                  <button
                    onClick={() => setSelectedMessage(null)}
                    aria-label="Close"
                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150 flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="space-y-2 mb-4">
                  <MetaRow label="From" value={`${selectedMessage.from_name || ''} <${selectedMessage.from_address}>`.trim()} />
                  {selectedMessage.campaign_name && (
                    <MetaRow label="Campaign" value={selectedMessage.campaign_name} />
                  )}
                  <MetaRow
                    label="Match"
                    value={`${selectedMessage.match_method || 'None'} (${selectedMessage.match_confidence})`}
                  />
                  {(selectedSentiment || selectedMessage.intent) && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {selectedSentiment && (
                        <>
                          <span
                            className="text-muted-foreground dark:text-[#8a8f98] uppercase"
                            style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
                          >
                            Sentiment
                          </span>
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border',
                              selectedSentiment.borderChipClass,
                            )}
                            style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.04em' }}
                          >
                            <selectedSentiment.Icon className="h-3 w-3 flex-shrink-0" weight="regular" aria-hidden="true" />
                            {selectedSentiment.label}
                          </span>
                        </>
                      )}
                      {/* The second tag — what they want, beside how they feel. */}
                      {selectedMessage.intent && (
                        <>
                          <span
                            className="text-muted-foreground dark:text-[#8a8f98] uppercase ml-1"
                            style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
                          >
                            Intent
                          </span>
                          <span
                            className={cn(
                              'inline-flex items-center px-2 py-0.5 rounded-full border',
                              intentChipClass(selectedMessage.intent),
                            )}
                            style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.04em' }}
                          >
                            {intentLabel(selectedMessage.intent)}
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Email body */}
                <div
                  className={cn(
                    'rounded-md p-4 mb-4 max-h-60 overflow-y-auto border',
                    'bg-muted/30 border-border/50',
                    'dark:bg-white/[0.02] dark:border-white/[0.06]',
                  )}
                >
                  <ReplyBodyView key={selectedMessage.id} body={selectedMessage.body_text} />
                </div>

                {/* Actions */}
                <div className="space-y-3">
                  {!selectedMessage.sentiment && canActOnInbox && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAnalyze(selectedMessage.id)}
                      disabled={analyzingId === selectedMessage.id || planLapsed}
                      title={lapsedTitle}
                      loading={analyzingId === selectedMessage.id}
                      icon={<Brain className="h-4 w-4" weight="regular" />}
                      className="w-full"
                    >
                      Analyze sentiment
                    </Button>
                  )}

                  {selectedMessage.auto_reply_pending && (
                    <div className="space-y-2">
                      <p
                        className="text-foreground dark:text-[#f7f8f8] uppercase"
                        style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
                      >
                        Pending auto-reply
                      </p>
                      <div
                        className={cn(
                          'rounded-md p-3 border whitespace-pre-wrap',
                          'bg-muted/30 border-border/50 text-foreground',
                          'dark:bg-white/[0.02] dark:border-white/[0.06] dark:text-[#d0d6e0]',
                        )}
                        style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.55 }}
                      >
                        {selectedMessage.auto_reply_content}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => approveReply(selectedMessage.id)}
                          disabled={planLapsed}
                          title={lapsedTitle}
                          icon={<CheckCircle className="h-4 w-4" weight="regular" />}
                          className="flex-1"
                        >
                          Approve &amp; send
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => rejectReply(selectedMessage.id)}
                          icon={<XCircle className="h-4 w-4" weight="regular" />}
                          className="flex-1"
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  )}

                  {!selectedMessage.auto_reply_pending && canActOnInbox && (
                    <div className="space-y-2">
                      <textarea
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        placeholder="Type a reply…"
                        rows={3}
                        className={cn(
                          'w-full px-3 py-2.5 rounded-md border transition-colors duration-150 resize-none',
                          'bg-background border-border/60 text-foreground placeholder:text-muted-foreground/50',
                          'hover:bg-background hover:border-border/80',
                          'focus:outline-none focus-visible:border-[#7170ff]/55',
                          'dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8] dark:placeholder:text-[#62666d]',
                          'dark:hover:bg-white/[0.03] dark:hover:border-white/[0.14]',
                          'dark:focus-visible:border-[#7170ff]/75',
                        )}
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px', lineHeight: 1.5 }}
                      />
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleSendReply(selectedMessage.id)}
                        disabled={!replyContent.trim() || sendingReply || planLapsed}
                        title={lapsedTitle}
                        loading={sendingReply}
                        icon={<PaperPlaneTilt className="h-4 w-4" weight="regular" />}
                        className="w-full"
                      >
                        Send reply
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card
              variant="default"
              className="rounded-lg border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08]"
            >
              <CardContent className="flex flex-col items-center justify-center py-20">
                <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-3">
                  <ChatCircle className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
                </div>
                <p
                  className="text-muted-foreground dark:text-[#8a8f98]"
                  style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                >
                  Select a message to view details
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Module-local helpers per §43

function TabPill({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean
  onClick: () => void
  label: string
  count?: number
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-colors duration-150',
        active
          ? 'bg-[#5e6ad2] text-white border-[#5e6ad2] dark:bg-[#5e6ad2] dark:border-[#5e6ad2]'
          : 'bg-muted/40 text-muted-foreground border-border/50 hover:bg-muted/70 hover:border-border/80 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06] dark:hover:bg-white/[0.06] dark:hover:border-white/[0.10]',
      )}
      style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
    >
      {label}
      {count != null && count > 0 && (
        <span
          className={cn(
            'inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full tabular-nums',
            active
              ? 'bg-white/20 text-white'
              : 'bg-amber-500/15 text-amber-700 dark:bg-amber-500/[0.18] dark:text-amber-400',
          )}
          style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '-0.05px' }}
        >
          {count}
        </span>
      )}
    </button>
  )
}

function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
}: {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
}) {
  const currentPage = page + 1
  const totalPages = Math.ceil(total / pageSize)
  const startItem = page * pageSize + 1
  const endItem = Math.min((page + 1) * pageSize, total)

  const getPageNumbers = (): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = []
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (currentPage > 3) pages.push('ellipsis')
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i)
      }
      if (currentPage < totalPages - 2) pages.push('ellipsis')
      pages.push(totalPages)
    }
    return pages
  }

  return (
    <div className="flex items-center justify-between py-2">
      <p
        className="text-muted-foreground dark:text-[#8a8f98]"
        style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
      >
        <span className="text-foreground dark:text-[#f7f8f8] font-medium tabular-nums">{startItem}-{endItem}</span> of{' '}
        <span className="text-foreground dark:text-[#f7f8f8] font-medium tabular-nums">{total}</span> messages
      </p>
      <div className="flex items-center gap-1">
        <button
          disabled={page === 0}
          onClick={() => onPageChange(page - 1)}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-100 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]"
          aria-label="Previous page"
        >
          <CaretLeft className="h-4 w-4" />
        </button>
        {getPageNumbers().map((p, idx) =>
          p === 'ellipsis' ? (
            <span key={`ellipsis-${idx}`} className="px-1.5 text-muted-foreground dark:text-[#62666d]" style={{ fontSize: '12px' }}>…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p - 1)}
              className={cn(
                'h-7 min-w-[1.75rem] px-2 rounded-md transition-colors duration-100 tabular-nums',
                p === currentPage
                  ? 'bg-[#5e6ad2] text-white shadow-sm dark:bg-[#5e6ad2]'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]',
              )}
              style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
            >
              {p}
            </button>
          ),
        )}
        <button
          disabled={(page + 1) * pageSize >= total}
          onClick={() => onPageChange(page + 1)}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-100 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]"
          aria-label="Next page"
        >
          <CaretRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

function EmptyCard({
  icon,
  headline,
  body,
}: {
  icon: React.ReactNode
  headline: string
  body: string
}) {
  return (
    <Card
      variant="default"
      className="rounded-lg border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08]"
    >
      <CardContent className="flex flex-col items-center justify-center py-14 px-6 text-center">
        <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-4">
          {icon}
        </div>
        <h3
          className="text-foreground dark:text-[#f7f8f8] mb-2"
          style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.33 }}
        >
          {headline}
        </h3>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed max-w-md mx-auto"
          style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
        >
          {body}
        </p>
      </CardContent>
    </Card>
  )
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <span
        className="text-muted-foreground dark:text-[#8a8f98] uppercase flex-shrink-0"
        style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em', lineHeight: 1.8 }}
      >
        {label}
      </span>
      <span
        className="text-foreground dark:text-[#f7f8f8] truncate"
        style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px', lineHeight: 1.6 }}
      >
        {value}
      </span>
    </div>
  )
}

export default InboxPage
